// Fase 4 · Orden y huecos de los eventos del hub `/hub/tpv`.
//
// Cada evento trae `seq` (monótono por restaurante) y `epoch` (cambia si el
// servidor reinicia y pierde su buffer). Reglas:
//   - seq === last+1       → se aplica (y se drenan los que estaban esperando).
//   - seq <= last          → duplicado/viejo, se ignora.
//   - seq  >  last+1       → hueco: se guarda un momento (`bufferMs`) por si
//                            el que falta llega desordenado; si no llega,
//                            `onGap(last)` para pedir `realtime/sync?since=last`.
//   - epoch distinto       → el servidor ya no puede reponer: `onResync`.
// Sin React ni Redux para poder testearlo.

export function createSeqTracker({
  onApply,
  onGap,
  onResync,
  bufferMs = 300,
  setTimer = (fn, ms) => setTimeout(fn, ms),
  clearTimer = (t) => clearTimeout(t),
} = {}) {
  let epoch = null;
  let lastSeq = null;
  const buffer = new Map();
  let timer = null;

  const apply = (env) => {
    try {
      onApply?.(env);
    } catch (err) {
      // Un handler roto no debe parar el flujo de eventos.
      if (process.env.NODE_ENV !== "production") console.warn("[realtime] handler error", err);
    }
  };

  const cancelTimer = () => {
    if (timer != null) {
      clearTimer(timer);
      timer = null;
    }
  };

  const drain = () => {
    while (lastSeq != null && buffer.has(lastSeq + 1)) {
      const next = buffer.get(lastSeq + 1);
      buffer.delete(lastSeq + 1);
      lastSeq += 1;
      apply(next);
    }
    // Lo que quede por debajo ya no sirve (llegó por otro camino).
    for (const s of Array.from(buffer.keys())) if (s <= lastSeq) buffer.delete(s);
    if (buffer.size === 0) cancelTimer();
  };

  const resetTo = (newEpoch, newSeq, reason) => {
    epoch = newEpoch ?? epoch;
    lastSeq = newSeq ?? lastSeq;
    buffer.clear();
    cancelTimer();
    onResync?.(reason);
  };

  const armGapTimer = () => {
    if (timer != null) return;
    timer = setTimer(() => {
      timer = null;
      if (buffer.size > 0 && lastSeq != null && !buffer.has(lastSeq + 1)) {
        onGap?.(lastSeq);
      }
    }, bufferMs);
  };

  return {
    /** Punto de partida (GET realtime/seq al conectar). No dispara nada. */
    init({ latestSeq, epoch: e } = {}) {
      if (epoch != null && e != null && e !== epoch) {
        resetTo(e, latestSeq, "epoch");
        return;
      }
      epoch = e ?? epoch;
      if (lastSeq == null || (latestSeq != null && latestSeq > lastSeq)) lastSeq = latestSeq ?? lastSeq;
      drain();
    },

    accept(env) {
      if (!env) return "ignored";
      const seq = typeof env.seq === "number" ? env.seq : null;
      if (seq == null) {
        apply(env);
        return "applied";
      }
      // seq 0 = evento solo-sesión (comensal QR / mesa vigilada): no forma
      // parte de la secuencia del restaurante. Se entrega, pero sin mover
      // `lastSeq` (un 0 antes del arranque fijaba lastSeq=0 y provocaba una
      // puesta al día innecesaria con el siguiente evento o baliza).
      if (seq <= 0) {
        apply(env);
        return "unsequenced";
      }
      if (epoch != null && env.epoch != null && env.epoch !== epoch) {
        resetTo(env.epoch, seq, "epoch");
        return "resync";
      }
      if (lastSeq == null) {
        epoch = env.epoch ?? epoch;
        lastSeq = seq;
        apply(env);
        drain();
        return "applied";
      }
      if (seq <= lastSeq) return "duplicate";
      if (seq === lastSeq + 1) {
        lastSeq = seq;
        apply(env);
        drain();
        return "applied";
      }
      buffer.set(seq, env);
      armGapTimer();
      return "buffered";
    },

    /** Respuesta de `GET realtime/sync?since=` → rellena el hueco o pide snapshot. */
    applySync(res) {
      if (!res) return "ignored";
      if (res.snapshotRequired || (epoch != null && res.epoch != null && res.epoch !== epoch)) {
        resetTo(res.epoch, res.latestSeq, "snapshot");
        return "resync";
      }
      epoch = res.epoch ?? epoch;
      for (const env of res.events || []) {
        if (typeof env?.seq === "number" && (lastSeq == null || env.seq > lastSeq)) buffer.set(env.seq, env);
      }
      if (lastSeq == null && buffer.size > 0) lastSeq = Math.min(...buffer.keys()) - 1;
      drain();
      // El servidor no pudo reponer un hueco que decía cubrir (≤ latestSeq):
      // estado completo. Lo que llegó en directo DESPUÉS de pedir el reenvío
      // (seq > latestSeq) no es un fallo del reenvío: se queda en el buffer
      // y, si sigue faltando algo, el temporizador pedirá otro reenvío.
      const covered = res.latestSeq ?? lastSeq;
      if (buffer.size > 0 && Array.from(buffer.keys()).some((k) => k <= covered)) {
        resetTo(res.epoch, res.latestSeq, "sync-incomplete");
        return "resync";
      }
      if (buffer.size > 0) armGapTimer();
      if (res.latestSeq != null && (lastSeq == null || res.latestSeq > lastSeq) && (res.events || []).length === 0) {
        // Nada que reponer pero el servidor va por delante (eventos que no
        // nos tocaban o ya caducados): alineamos sin reaplicar.
        lastSeq = res.latestSeq;
      }
      return "synced";
    },

    getState() {
      return { epoch, lastSeq, buffered: Array.from(buffer.keys()).sort((a, b) => a - b) };
    },

    dispose() {
      cancelTimer();
      buffer.clear();
    },
  };
}
